










































































import {
  EnterBreakNode,
  InputDocNode,
  ParagraphNode,
  ProsemirrorHelper,
  ProsemirrorType,
  TextPlaceholder,
} from '@getaccept/editor-lib';
import { MentionNode } from '@getaccept/editor-lib/src/tiptap/nodes/mention-node';
import type { Emoji } from '@getaccept/lib-ui';
import { AvatarSize } from '@getaccept/lib-ui';
import History from '@tiptap/extension-history';
import Text from '@tiptap/extension-text';
import { EditorContent, Extension, Editor as TipTapEditor } from '@tiptap/vue-2';
import type { ComputedRef, PropType, Ref } from '@vue/composition-api';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import debounce from 'debounce';
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import type { EditorState, Transaction } from 'prosemirror-state';
import { ChatHelper } from '../../chat/helpers/chat.helper';
import { EmojiHelper } from '../../emoji/helpers/emoji.helper';
import { t } from '../../helpers/translation.helper';
import LinkButton from '../components/LinkButton.vue';
import MentionParticipants from '../mentions/components/MentionParticipants.vue';
import { useMentionsKeybindings } from '../mentions/composables/mentions-keybindings.composable';
import { MentionsHelper } from '../mentions/helpers/mentions.helper';
import type { MentionParticipant } from '../mentions/types/mention-participant';
import type { CommentParticipant } from '../types/comment-participant';
import type { CurrentUser } from '../types/current-user';

export default defineComponent({
  components: {
    LinkButton,
    EditorContent,
    MentionParticipants,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      default: () => ({ name: '', thumbUrl: '' }),
    },
    mentionParticipants: {
      type: Array as PropType<MentionParticipant[]>,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '',
    },
    isTemporary: {
      type: Boolean,
    },
    dataExternal: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
    },
    mentionsEnabled: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const editor: Ref<TipTapEditor | null> = ref(null);
    const emojiIndex: Ref<EmojiIndex> = ref(null);
    const showEmojis = ref(false);

    const hasInput = ref(!!props.value);

    const setFocus = () => {
      if (editor.value?.view.hasFocus()) {
        return;
      }
      editor.value.commands.focus();
    };

    const showMentionParticipants = ref(false);
    const participantsQuery = ref('');

    const hideMentionParticipants = () => {
      showMentionParticipants.value = false;
      participantsQuery.value = '';
      editor.value.commands.focus();
    };

    const evaluateShowMentionParticipants = () => {
      if (!props.mentionsEnabled) {
        return;
      }
      const { query = '', show } = MentionsHelper.shouldShowMentionParticipants(editor.value);
      participantsQuery.value = query;
      showMentionParticipants.value = show;
    };

    const debounceShowMentionParticipants = debounce(evaluateShowMentionParticipants, 100);

    const mentionListOffset = 4;

    watch(
      () => props.value,
      () => {
        editor.value.commands.setContent(parsedContent.value);
      }
    );

    const mentionListBottomOffset: ComputedRef<number> = computed(() => {
      if (!showMentionParticipants.value) {
        return 0;
      }
      const top = editorContainer.value?.getBoundingClientRect().height;
      return top + mentionListOffset;
    });

    const handleInput = () => {
      if (props.isTemporary) {
        return;
      }
      debounceShowMentionParticipants();
      const richText = JSON.stringify(editor.value.getJSON());
      hasInput.value = !!editor.value.getText();
      const text = ProsemirrorHelper.getInnerTextContent(editor.value.state);

      if (richText === props.value) {
        return;
      }
      emit('input', { richText, text });
    };

    const setShowEmojiPicker = (show: boolean) => {
      showEmojis.value = show;
    };

    const handleAddEmoji = ({ native }: Emoji) => {
      setShowEmojiPicker(false);
      editor.value.commands.insertContent(native);
      setFocus();
    };

    const handleSubmit = () => {
      if (!hasInput.value) {
        return;
      }

      emit('submit', {
        richText: JSON.stringify(editor.value.getJSON()),
        text: ProsemirrorHelper.getInnerTextContent(editor.value.state),
      });

      editor.value.commands.setContent('');
      hasInput.value = false;
    };

    const clickOutside = () => {
      setShowEmojiPicker(false);
    };

    const parsedContent = computed(() => {
      // TODO: Delete all comments with plain text in DB, remove this try catch
      let content;
      try {
        content = JSON.parse(props.value);
      } catch (e) {
        content = props.value;
      }
      return content;
    });

    const shouldFocus = computed(() => props.selected && !props.isTemporary);

    onMounted(async () => {
      editor.value = new TipTapEditor({
        content: parsedContent.value,
        enablePasteRules: false,
        extensions: [
          InputDocNode,
          ParagraphNode,
          Text,
          History,
          EnterBreakNode,
          TextPlaceholder.configure({ placeholder: props.placeholder }),
          MentionNode.configure({ mentionParticipants: props.mentionParticipants }),
          Extension.create({
            addKeyboardShortcuts() {
              return {
                'Mod-Enter'() {
                  handleSubmit();
                  return true;
                },
              };
            },
          }),
        ],
        onSelectionUpdate: debounceShowMentionParticipants,
        onUpdate: handleInput,
        onCreate: () => {
          if (shouldFocus.value) {
            editor.value?.view.focus();
          }
        },
      });

      emojiIndex.value = await EmojiHelper.emojiIndex();
    });

    const getParticipantId = (commentParticipant: CommentParticipant) =>
      commentParticipant.recipientId || commentParticipant.userId;

    const createMentionId = (commentParticipant: CommentParticipant) => {
      const type: string = commentParticipant.recipientId ? 'recipientId' : 'userId';
      const id = getParticipantId(commentParticipant);
      return `<${type}:${id}>`;
    };

    const addMention = (commentParticipant: CommentParticipant, selectionFromOffset = 0) => {
      const { tr, schema, selection }: EditorState = editor.value.state;

      const node = schema.nodes[ProsemirrorType.Mention].create({
        ['data-mention-participant-id']: getParticipantId(commentParticipant),
        ['data-mention-id']: createMentionId(commentParticipant),
      });

      // Replace the query text and the @ symbol with the actual mention
      const selectionFrom =
        selection.from - participantsQuery.value.length - 1 - selectionFromOffset;

      const transaction: Transaction = tr.replaceRangeWith(selectionFrom, selection.to, node);
      editor.value.view.dispatch(transaction);
      hideMentionParticipants();
      editor.value.commands.focus();
    };

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    const editorContainer = ref(null);

    const filteredMentionParticipants = computed(() =>
      props.mentionParticipants.filter((mentionParticipant: MentionParticipant) =>
        mentionParticipant.fullName.toLowerCase().includes(participantsQuery.value.toLowerCase())
      )
    );

    const { handlePressUp, handlePressDown, selectedIndex } = useMentionsKeybindings(
      filteredMentionParticipants,
      showMentionParticipants
    );

    const handleEnterPress = (event: KeyboardEvent) => {
      if (!showMentionParticipants.value) {
        return;
      }
      // Enter causes one additional selection hence selectionOffset 1
      addMention(filteredMentionParticipants.value[selectedIndex.value].commentPartcipiant, 1);
      event.preventDefault();
    };

    const handleEscapePress = (event: KeyboardEvent) => {
      if (!showMentionParticipants.value) {
        return;
      }
      hideMentionParticipants();
      event.preventDefault();
    };

    return {
      AvatarSize,
      editor,
      emojiIndex,
      showEmojis,
      emojiLocalization: ChatHelper.getEmojiLocalization(),
      hasInput,
      showMentionParticipants,
      editorContainer,
      clickOutside,
      setFocus,
      setShowEmojiPicker,
      handleAddEmoji,
      handleSubmit,
      t,
      addMention,
      hideMentionParticipants,
      mentionListBottomOffset,
      filteredMentionParticipants,
      handlePressUp,
      handlePressDown,
      handleEnterPress,
      handleEscapePress,
      selectedIndex,
    };
  },
});
